import Box from "@mui/material/Box";
import { useCallback, useEffect, useState } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { useSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import { useTranslation } from "react-i18next";
import {
  Button,
  CircularProgress,
  Divider,
  Grid,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import { checkPrivileges } from "Helpers/CheckPrivileges";
import { useNavigate } from "react-router-dom";
import FormDialog from "common/Modal/Modal";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import { ReactComponent as Packages } from "assets/SideBarIcons/pakage.svg";
import BackendPaginationTable from "common/Table/TableWithPagination";
import { PackagesProfileService } from "subModule/src/core/services";
import { DeleteData, Wrapper } from "subModule/src/type";
import Loader from "layout/Loader";
import { useGetPackagesService } from "subModule/src/services/packages/usePackages";
import { ChangeStatusPackagesResponse } from "subModule/src/services/packages/interface";
import PackagesSearchHeaderMenu from "components/Packages/PackagesSearchHeaderMenu";
import PackageBulkUpdateMenu from "components/Packages/PackageBulkUpdateMenu";
import { CommonProfileService } from "subModule/src/core/services";
import { themes } from "theme/themes";
import { useSelector } from "react-redux";
import { brandingInfo } from "features/server/brandingSlice";
import PackageGroupPackageSearchHeader from "components/PackageGroup/PackageGroupPackageSearchHeader";
import DynamicSelect from "common/Select/Select";
import {
  getSortParamFromColumn,
  PACKAGE_SORT_FIELD_MAP,
  SortOrder,
} from "Helpers/buildApiSortParam";

const PACKAGE_SORTABLE_COLUMNS = ["packageNameCode"];
interface SearchFormData {
  [key: string]: string | any;
}
export default function PackagesListing() {
  const navigate = useNavigate();
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  const [openDeleteModal, setDeleteModal] = useState(false);
  const [pageNo, setPageNo] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [statusModal, setStatusModal] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [filter, setFilter] = useState<string[]>(["title"]);
  const [search, setSearch] = useState<any>({});
  const [sort, setSort] = useState<string | undefined>();
  const [sortOrderBy, setSortOrderBy] = useState("");
  const [sortOrder, setSortOrder] = useState<SortOrder>("asc");
  const [searchFormData, setSearchFormData] = useState<SearchFormData>({
    title: "",
    packageCode: "",
  });
  const [row, setRow] = useState<any>({});
  const [downloading, setDownloading] = useState<boolean>(false);
  const [rows, setRows] = useState<any>([]);
  const [multiActionModal, setMultiActionModal] = useState(false);
  const [actionType, setActionType] = useState<{
    label: string;
    value: number;
  } | null>(null);
  const {
    data: tableData,
    isFetching,
    refetch,
  } = useGetPackagesService({
    pageNumber: pageNo,
    size: pageSize,
    ...search,
    sort,
  });
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const fetchData = useCallback(() => {
    refetch().then((res: any) => {
      if (!res?.data?.pages[0]?.success)
        enqueueSnackbar(
          <AlertMessages
            text={res?.data?.pages[0]?.message || "Something went wrong"}
          />,
          { variant: "error" },
        );
    });
  }, []);
  useEffect(() => {
    fetchData();
  }, [pageNo, pageSize, search, sort]);
  const handleChangePage = (pageNo: number) => {
    setPageNo(pageNo + 1);
  };
  const handleChangeRows = (pageSize: number) => {
    setPageSize(pageSize);
    setPageNo(1);
  };
  const onEdit = (row: any) => navigate("/package/" + row.id);
  const showRowView = (row: any) => navigate("/package/" + row.id);
  const onDelete = (row: any) => {
    setDeleteModal(true);
    setRow(row);
  };
  const clearSearch = () => {
    setFilter(["title"]);
    setSearchFormData({ title: "", packageCode: "" });
    if (Object.keys(search).length) {
      setSearch({});
    }
  };
  const handleChangeFilter = (event: SelectChangeEvent<typeof filter>) => {
    const {
      target: { value },
    } = event;
    setFilter(typeof value === "string" ? value.split(",") : value);
    setSearchFormData((prevFormData) =>
      Object.fromEntries(
        Object.keys(prevFormData).map((key) => [
          key,
          value.includes(key) ? prevFormData[key] : "",
        ]),
      ),
    );
    if (!value.length && Object.keys(search).length) {
      clearSearch();
    }
  };
  const handleChangeSearch = (field: string, value: any) => {
    const val = value;
    setSearchFormData((prevData) => ({
      ...prevData,
      [field]: val,
    }));
  };
  const handleSubmitSearch = () => {
    const filteredSearch: Record<string, string> = {};
    Object.entries(searchFormData).forEach(([key, value]) => {
      if (value) {
        filteredSearch[key] = value?.trim();
      }
    });
    if (
      Object.keys(filteredSearch).length > 0 &&
      Object.values(filteredSearch).some((value) => value)
    ) {
      setSearch(filteredSearch);
      setPageNo(1);
      setPageSize(pageSize);
    }
  };
  const onChangeStatus = (row: any) => {
    setRow(row);
    setStatusModal(true);
  };
  const handleSortChange = (orderBy: string, order: SortOrder) => {
    const nextSort = getSortParamFromColumn(
      orderBy,
      order,
      PACKAGE_SORT_FIELD_MAP,
    );
    if (!nextSort) return;
    setSortOrderBy(orderBy);
    setSortOrder(order);
    setSort(nextSort);
    setPageNo(1);
  };
  const actionslist = [
    { label: "edit", onClick: onEdit, icon: <EditIcon /> },
    { label: "delete", onClick: onDelete, icon: <DeleteIcon /> },
  ];
  const adjustActions = (actions: any[]) => {
    return checkPrivileges("ROLE_PACKAGE_UPDATE")
      ? checkPrivileges("ROLE_PACKAGE_ARCHIVE")
        ? actions
        : actions.filter((action) => action.label !== "delete")
      : checkPrivileges("ROLE_PACKAGE_ARCHIVE")
        ? actions.filter((action) => action.label !== "edit")
        : [];
  };
  const DownloadPackages = () => {
    CommonProfileService.exportEntityWithParams(
      { ...search },
      "packages/export/excel",
    )
      .then((res: Blob) => {
        const blob = new Blob([res], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const url = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `Packages.xlsx`);
        document.body.appendChild(link);
        link.click();

        link.remove();
        window.URL.revokeObjectURL(url);
        enqueueSnackbar(<AlertMessages text={t("downloadedSuccessfully")} />, {
          variant: "success",
        });
      })
      .catch((err) => {
        console.error("===", err);
        enqueueSnackbar(<AlertMessages statusCode={0} />, { variant: "error" });
      })
      .finally(() => {
        setDownloading(false);
      });
  };
  const handleMultiAction = (isChecked: boolean, row: any) => {
    setRows((prev: any) => {
      const next = isChecked
        ? [...prev, row].filter(
            (v, i, a) => a.findIndex((t) => t.id === v.id) === i,
          )
        : prev.filter((r: any) => r.id !== row.id);
      if (!next.length) setActionType(null);
      return next;
    });
  };
  return (
    <Box>
      <ComponentHeaderTwo
        showBtn={true}
        showSaveBtn={false}
        onClick={() => navigate("/package/create")}
        heading={t("packages")}
        icon={<Packages height={28} width={28} />}
        btnText={"create"}
        btnType={"create"}
        showCreateBtn={checkPrivileges("ROLE_PACKAGE_CREATE")}
      />
      {isFetching || loading ? (
        <Loader />
      ) : (
        <BackendPaginationTable
          columns={[
            { id: "multiAction", label: "", numeric: false, width: "46px" },
            {
              id: "packageNameCode",
              label: "namePackageCode",
              numeric: false,
              width: "28%",
            },
            {
              id: "pricingTypePackages",
              label: "pricingType",
              numeric: false,
              width: "18%",
            },
            {
              id: "itemGroup",
              label: t("partType"),
              numeric: false,
              width: "20%",
            },
            ...(checkPrivileges("ROLE_PACKAGE_ACTIVE")
              ? [
                  {
                    id: "active",
                    label: "status",
                    numeric: false,
                    width: "10%",
                  },
                ]
              : []),
            ...(adjustActions(actionslist).length
              ? [
                  {
                    id: "actions",
                    label: "actions",
                    numeric: false,
                    width: "5%",
                  },
                ]
              : []),
          ]}
          data={tableData?.pages[0]?.data?.content || []}
          pageNo={pageNo}
          pageSize={pageSize}
          tableData={tableData?.pages[0]?.data || {}}
          showCheckbox={false}
          showActions={true}
          enableSorting={true}
          serverSideSorting={true}
          sortOrderBy={sortOrderBy}
          sortOrder={sortOrder}
          onSortChange={handleSortChange}
          sortableColumnIds={PACKAGE_SORTABLE_COLUMNS}
          showHeader={true}
          showSearch={true}
          showFilter={true}
          showCreateButton={true}
          actions={adjustActions(actionslist)}
          handleChangePage={handleChangePage}
          handleChangeRows={handleChangeRows}
          onChangeStatus={onChangeStatus}
          rowClick={checkPrivileges("ROLE_PACKAGE_UPDATE")}
          showRowView={showRowView}
          loadingSearch={false}
          handleMultiAction={handleMultiAction}
          selectedRows={rows}
          headerComponent={
            <>
              {rows?.length ? (
                <Box
                  className="me-[10px] flex gap-[10px]"
                  sx={{
                    "& .commonSelect .MuiTypography-root": {
                      fontSize: "11px !important",
                      color: "rgba(0, 0, 0, 0.6) !important",
                      marginBottom: "-4px",
                    },
                    "& .dropdown": { height: "13px !important" },
                    "& .css-7896aj-control, & .css-1xiok1u-control": {
                      width: "150px",
                      overflow: "hidden",
                      fontSize: "12px",
                      "& .css-coi8w6-indicatorContainer, & .css-8q9606-indicatorContainer":
                        {
                          background: "#fff",
                          padding: 0,
                          width: "18px",
                          height: "18px",
                        },
                    },
                  }}
                >
                  <DynamicSelect
                    label={
                      t("action") +
                      " (" +
                      rows.length +
                      " " +
                      t("selected") +
                      ")"
                    }
                    isClearable
                    options={[
                      { value: 1, label: t("active") },
                      { value: 2, label: t("inactive") },
                      { value: 3, label: t("delete") },
                    ]}
                    onChange={(selectedOption: any) => {
                      if (selectedOption) {
                        setActionType(selectedOption);
                        setMultiActionModal(true);
                      } else setActionType(null);
                    }}
                    value={actionType}
                  />
                  <Divider
                    className="w-[1px] h-[36px] mt-[15px]"
                    sx={{ background: "#DFD2E1" }}
                    orientation="vertical"
                    flexItem
                  />
                </Box>
              ) : null}
              <Box className="table-header-search">
                <PackageGroupPackageSearchHeader
                  searchFormData={searchFormData}
                  handleChangeFilter={handleChangeFilter}
                  handleChangeSearch={handleChangeSearch}
                  handleSubmitSearch={handleSubmitSearch}
                  clearSearch={clearSearch}
                  search={search}
                  {...{ filter }}
                />
              </Box>
              <Box className="table-header-actions">
                {checkPrivileges("ROLE_PACKAGE_EXPORT") ? (
                  <Button
                    id="import-button"
                    variant="contained"
                    disableElevation
                    onClick={() => {
                      setDownloading(true);
                      DownloadPackages();
                    }}
                    className="bg-white font-Saira font-[600] text-[13px] px-4 py-1 cursor-pointer rounded-[6px]"
                    sx={{
                      outline: "2px solid",
                      color: themeColors?.secondary,
                      borderColor: themeColors?.secondary,
                      height: 34,
                      minHeight: 34,
                    }}
                    disabled={downloading}
                  >
                    {downloading ? (
                      <CircularProgress
                        sx={{
                          color: themeColors?.secondary,
                          marginRight: "8px",
                        }}
                        size={16}
                      />
                    ) : null}
                    {t("export")}
                  </Button>
                ) : null}
                {checkPrivileges("ROLE_PACKAGE_IMPORT") ? (
                  <PackagesSearchHeaderMenu refetch={refetch} />
                ) : null}
                {checkPrivileges("ROLE_PACKAGE_UPDATE") ? (
                  <PackageBulkUpdateMenu
                    refetch={refetch}
                    selectedRows={rows}
                    pageRows={tableData?.pages[0]?.data?.content || []}
                  />
                ) : null}
              </Box>
            </>
          }
        />
      )}
      {openDeleteModal && (
        <FormDialog
          open={openDeleteModal}
          onClose={() => setDeleteModal(false)}
          title={t("delete") + " " + t("package")}
          content={
            <Grid container>
              <Grid item xs={12}>
                <Typography>{t("Are you sure you want to delete?")}</Typography>
              </Grid>
            </Grid>
          }
          saveFunction={() => {
            setDeleteModal(false);
            setLoading(true);
            PackagesProfileService.deletePackages((row as any).id)
              .then((response: DeleteData) => {
                if (response.success) {
                  setRows((prev: any) =>
                    prev.filter((r: any) => r.id !== row.id),
                  );
                  if (
                    tableData?.pages[0]?.data?.content?.length === 1 &&
                    pageNo > 1
                  )
                    setPageNo(1);
                  else refetch();
                  enqueueSnackbar(
                    <AlertMessages
                      statusCode={response.status}
                      text={
                        t("package") +
                        " " +
                        t("deleted") +
                        " " +
                        t("successfully")
                      }
                    />,
                    { variant: "success" },
                  );
                } else {
                  enqueueSnackbar(
                    <AlertMessages statusCode={response.status} />,
                    { variant: "error" },
                  );
                }
              })
              .catch((err: any) => {
                console.error("===", err);
                enqueueSnackbar(
                  <AlertMessages
                    text={err?.data?.message || err || "Something went wrong"}
                  />,
                  { variant: "error" },
                );
              })
              .finally(() => setLoading(false));
          }}
          submitButtonText="Delete"
        />
      )}
      {statusModal && (
        <FormDialog
          open={statusModal}
          onClose={() => setStatusModal(false)}
          title="Change Status"
          // icon={<div><Home/></div>}
          content={
            <Grid container>
              <Grid item xs={12}>
                <Typography>{t(`Are you sure?`)}</Typography>
              </Grid>
            </Grid>
          }
          saveFunction={() => {
            setStatusModal(false);
            setLoading(true);
            PackagesProfileService.changeStatusPackages(
              (row as any).id,
              row.active ? "inactive" : "active",
              {},
            )
              .then((response: ChangeStatusPackagesResponse) => {
                if (response.success) {
                  refetch();
                  enqueueSnackbar(
                    <AlertMessages
                      statusCode={response.status}
                      text={
                        t("status") +
                        " " +
                        t("changed") +
                        " " +
                        t("successfully")
                      }
                    />,
                    { variant: "success" },
                  );
                } else {
                  enqueueSnackbar(
                    <AlertMessages statusCode={response.status} />,
                    { variant: "error" },
                  );
                }
              })
              .catch((err: any) => {
                console.error("===", err);
                enqueueSnackbar(
                  <AlertMessages
                    text={err?.data?.message || err || "Something went wrong"}
                  />,
                  { variant: "error" },
                );
              })
              .finally(() => setLoading(false));
          }}
          submitButtonText="OK"
        />
      )}
      {multiActionModal && (
        <FormDialog
          open={multiActionModal}
          onClose={(event: any, reason: any) => {
            if (event && reason !== "backdropClick") {
              setMultiActionModal(false);
              setActionType(null);
            }
          }}
          title={
            actionType?.value === 3
              ? t("delete") + " " + t("packages")
              : t("Change Status") + " " + t("packages")
          }
          content={
            <Grid container>
              <Grid item xs={12}>
                <Typography>
                  {actionType?.value === 3
                    ? t("Are you sure you want to delete?")
                    : t("Are you sure?")}
                </Typography>
              </Grid>
            </Grid>
          }
          saveFunction={() => {
            setMultiActionModal(false);
            setLoading(true);
            CommonProfileService.multiActions({
              ids: rows.map((r: any) => r.id),
              action: actionType?.value || 0,
              typeName: "packages/bulk/action",
            })
              .then((response: Wrapper) => {
                if (response.success) {
                  setRows([]);
                  setActionType(null);
                  refetch();
                  enqueueSnackbar(
                    <AlertMessages
                      statusCode={response.status}
                      text={
                        actionType?.value === 3
                          ? t("packages") +
                            " " +
                            t("deleted") +
                            " " +
                            t("successfully")
                          : t("statuses") +
                            " " +
                            t("changed") +
                            " " +
                            t("successfully")
                      }
                    />,
                    { variant: "success" },
                  );
                } else {
                  enqueueSnackbar(
                    <AlertMessages statusCode={response.status} />,
                    { variant: "error" },
                  );
                }
              })
              .catch((err: any) => {
                console.error("===", err);
                enqueueSnackbar(
                  <AlertMessages
                    text={err?.data?.message || err || "Something went wrong"}
                  />,
                  { variant: "error" },
                );
              })
              .finally(() => setLoading(false));
          }}
          submitButtonText={actionType?.value === 3 ? t("Delete") : t("OK")}
        />
      )}
    </Box>
  );
}
